import React, { useState, useEffect, useCallback } from 'react';
import { Cpu, RefreshCw } from 'lucide-react';
import { puntovService } from '../../../services/api';
import Modal from './Modal';
import { fmtUsd, fmtPeriodo, fmtFechaHora } from './formato';

// Por qué una máquina no tiene monto este mes. Lo decide Punto V.
const SIN_COBRO = {
  sin_ventas: 'Sin ventas este mes',
  sin_tarifa: 'Sin tarifa configurada',
  inactiva_sin_ventas: 'Inactiva y sin ventas este mes',
  inactiva_antes_del_dia: 'Inactiva: dejó de vender antes del día mínimo'
};

const CONCEPTO = {
  tarifa_fija: 'Tarifa fija',
  comision_ventas: '% de ventas'
};

// Qué máquinas de un cliente entran en su mensualidad. El monto es lo que
// Punto V cobraría por cada una con las ventas del mes en curso; la elección
// se guarda y se respeta cada día 1 hasta que se cambie.
const DialogoMaquinasCobro = ({ cliente, puedeEditar, onClose, onCambio }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [excluyendo, setExcluyendo] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const res = await puntovService.maquinasACobrar(cliente.base_datos);
      setDatos(res.data);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las máquinas');
    } finally {
      setCargando(false);
    }
  }, [cliente.base_datos]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async (idMaquina, cobrar, motivoTexto = null) => {
    setError('');
    setAviso('');
    setGuardando(idMaquina);
    try {
      const res = await puntovService.guardarMaquinaACobrar(cliente.base_datos, idMaquina, cobrar, motivoTexto);
      setDatos(res.data);
      setAviso(res.message);
      setExcluyendo(null);
      setMotivo('');
      onCambio?.();
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
    } finally {
      setGuardando(null);
    }
  };

  const aCobrar = datos?.maquinas.filter(m => m.cobrar).length ?? 0;

  return (
    <Modal
      titulo={`Máquinas a cobrar de ${cliente.slug}`}
      subtitulo={`${cliente.duenio.nombre} · ${cliente.base_datos}`}
      icono={Cpu}
      onClose={onClose}
      bloqueado={guardando !== null}
      ancho="xl"
      pie={(
        <>
          <button type="button" onClick={cargar} disabled={cargando || guardando !== null} className="px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-100 flex items-center gap-1.5 disabled:opacity-50">
            <RefreshCw size={14} className={cargando ? 'animate-spin' : ''} /> Actualizar
          </button>
          <button type="button" onClick={onClose} disabled={guardando !== null} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">Cerrar</button>
        </>
      )}
    >
      {cargando && !datos && (
        <div className="text-center py-10"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
      )}

      {datos && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
            <div className="text-sm text-blue-900">
              <div className="font-medium">Mensualidad de {fmtPeriodo(datos.periodo)} (estimado a la fecha)</div>
              <div className="text-xs text-blue-800">
                Se cobran {aCobrar} de {datos.maquinas.length} máquinas. El monto final se calcula el día 1 con las ventas del mes completo.
              </div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold tabular-nums text-blue-900">{fmtUsd(datos.estimado_total_usd)}</div>
              {datos.estimado_total_usd !== datos.estimado_sin_exclusiones_usd && (
                <div className="text-xs text-blue-800">Sin excluir ninguna: {fmtUsd(datos.estimado_sin_exclusiones_usd)}</div>
              )}
            </div>
          </div>

          {datos.cortesia && (
            <p className="text-sm rounded-lg border border-teal-200 bg-teal-50 text-teal-800 p-3">
              {fmtPeriodo(datos.periodo)} está en cortesía: ese mes no se cobra ninguna máquina.
            </p>
          )}

          {aviso && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{aviso}</div>}

          <div className="rounded-xl border border-gray-200 divide-y">
            {datos.maquinas.map((m) => (
              <div key={m.id_maquina} className={`p-3 space-y-2 ${m.cobrar ? '' : 'bg-gray-50'}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-gray-800">{m.id_maquina}</span>
                      {!m.activa && <span className="text-xs px-1.5 py-0.5 rounded border border-gray-300 text-gray-500">Inactiva</span>}
                    </div>
                    <div className="text-xs text-gray-500">{m.descripcion || 'Sin descripción'}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      {m.estimado ? (
                        <>
                          <div className={`font-semibold tabular-nums ${m.cobrar ? 'text-gray-800' : 'text-gray-400 line-through'}`}>{fmtUsd(m.estimado.monto_usd)}</div>
                          <div className="text-xs text-gray-500">
                            {CONCEPTO[m.estimado.concepto] || m.estimado.concepto}
                            {m.estimado.base_usd != null && (m.estimado.concepto === 'tarifa_fija'
                              ? ` · el ${Number(m.estimado.porcentaje)}% de ${fmtUsd(m.estimado.base_usd)} no llega al mínimo`
                              : ` · ${Number(m.estimado.porcentaje)}% de ${fmtUsd(m.estimado.base_usd)}`)}
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-gray-400">{SIN_COBRO[m.sin_cobro] || 'Sin monto este mes'}</div>
                      )}
                    </div>
                    <label className={`flex items-center gap-2 text-sm ${puedeEditar ? 'cursor-pointer' : 'opacity-60'}`}>
                      <input
                        type="checkbox"
                        role="switch"
                        checked={m.cobrar}
                        disabled={!puedeEditar || guardando !== null}
                        onChange={() => {
                          setAviso('');
                          if (m.cobrar) {
                            setMotivo('');
                            setExcluyendo(m.id_maquina);
                          } else {
                            guardar(m.id_maquina, true);
                          }
                        }}
                      />
                      <span className={m.cobrar ? 'text-green-700' : 'text-gray-500'}>{m.cobrar ? 'Cobrar' : 'No cobrar'}</span>
                    </label>
                  </div>
                </div>

                {!m.cobrar && m.exclusion && (
                  <p className="text-xs text-gray-600">
                    No se cobra desde el {fmtFechaHora(m.exclusion.fecha)}{m.exclusion.por ? ` (${m.exclusion.por})` : ''}{m.exclusion.motivo ? ` · ${m.exclusion.motivo}` : ''}
                  </p>
                )}

                {excluyendo === m.id_maquina && (
                  <form
                    onSubmit={(e) => { e.preventDefault(); guardar(m.id_maquina, false, motivo.trim()); }}
                    className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2"
                  >
                    <p className="text-xs text-amber-900">
                      Desde ahora {m.id_maquina} no entra en la mensualidad ni en el recargo por mora, hasta que la vuelvas a marcar.
                    </p>
                    <input
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      maxLength={255}
                      autoFocus
                      required
                      placeholder="Motivo (obligatorio), ej. máquina en prueba"
                      className="w-full h-9 px-3 border border-amber-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400 outline-none"
                    />
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setExcluyendo(null)} disabled={guardando !== null} className="px-3 py-1.5 text-xs text-gray-700 rounded-lg hover:bg-white">Cancelar</button>
                      <button type="submit" disabled={guardando !== null || motivo.trim().length < 3} className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                        {guardando === m.id_maquina ? 'Guardando…' : 'No cobrar esta máquina'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ))}
            {datos.maquinas.length === 0 && <p className="p-6 text-center text-sm text-gray-500">Este cliente no tiene máquinas registradas.</p>}
          </div>
        </div>
      )}

      {error && <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
    </Modal>
  );
};

export default DialogoMaquinasCobro;
